import { useEffect, useState } from "react";
import { list, remove, update, create } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import EventSelect from "@/components/EventSelect";
import { StatusBadge } from "@/components/StatusBadge";
import { rundownStatus, RundownBadge } from "@/lib/rundownStatus";
import { ResourceForm } from "@/components/ResourcePage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useEventOptions } from "@/hooks/useEventOptions";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/constants";
import { Plus, Pencil, Trash2, Printer, Download, ChevronUp, ChevronDown, Zap } from "lucide-react";
import { toast } from "sonner";
import { downloadPdf } from "@/lib/pdf";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));

export default function Rundown() {
  const { can } = useAuth();
  const { events } = useEventOptions();
  const [eventId, setEventId] = useState("");
  const [items, setItems] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [qa, setQa] = useState({ hour: "08", minute: "00", endHour: "", endMinute: "", activity: "", location: "", pic: "", notes: "" });
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(t); }, []);

  useEffect(() => { if (!eventId && events.length) setEventId(events[0].id); }, [events]); // eslint-disable-line

  const load = () => {
    if (!eventId) return;
    list("rundown", { event_id: eventId }).then((d) => setItems(d.sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || String(a.time).localeCompare(String(b.time))))).catch(() => setItems([]));
  };
  useEffect(load, [eventId]); // eslint-disable-line

  const quickAdd = async () => {
    if (!eventId) { toast.error("Pilih event dulu"); return; }
    if (!qa.activity.trim()) { toast.error("Nama aktivitas wajib diisi"); return; }
    const time = `${qa.hour}:${qa.minute}`;
    const end = qa.endHour ? `${qa.endHour}:${qa.endMinute || "00"}` : "";
    await create("rundown", { event_id: eventId, time, end_time: end, activity: qa.activity, location: qa.location, pic: qa.pic, notes: qa.notes, status: "Pending", order: items.length });
    toast.success("Rundown ditambahkan");
    setQa({ hour: qa.hour, minute: qa.minute, endHour: "", endMinute: "", activity: "", location: "", pic: "", notes: "" });
    load();
  };

  const del = async (id) => { await remove("rundown", id); toast.success("Dihapus"); load(); };
  const cycleStatus = async (r) => {
    const next = r.status === "Done" ? "Pending" : "Done";
    setItems((p) => p.map((x) => (x.id === r.id ? { ...x, status: next } : x)));
    await update("rundown", r.id, { status: next });
  };

  const swap = async (i, j) => {
    if (j < 0 || j >= items.length) return;
    const a = items[i], b = items[j];
    await Promise.all([update("rundown", a.id, { order: j }), update("rundown", b.id, { order: i })]);
    load();
  };

  const ev = events.find((e) => e.id === eventId);
  const fields = [
    { name: "time", label: "Jam Mulai (HH:MM)", required: true },
    { name: "end_time", label: "Jam Selesai (HH:MM)" },
    { name: "activity", label: "Aktivitas", required: true, full: true },
    { name: "pic", label: "PIC" },
    { name: "location", label: "Lokasi" },
    { name: "vendor", label: "Vendor" },
    { name: "status", label: "Status", type: "select", options: ["Pending", "Done"], default: "Pending" },
    { name: "order", label: "Urutan", type: "number", default: items.length },
  ];

  return (
    <div>
      <PageHeader overline="Susunan Acara" title="Rundown" subtitle="Susun rundown detail wedding, urutkan, dan cetak untuk tim.">
        <Button variant="outline" onClick={() => downloadPdf("rundown-print-area", `Rundown-${ev ? ev.groom + "-" + ev.bride : "event"}.pdf`, "landscape")} data-testid="rundown-download-pdf" className="rounded-full"><Download className="w-4 h-4 mr-1" />PDF</Button>
        <Button variant="outline" onClick={() => window.print()} data-testid="rundown-print" className="rounded-full"><Printer className="w-4 h-4 mr-1" />Print</Button>
        {can(2) && <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="rundown-add-btn" className="bg-burgundy hover:bg-burgundy-dark text-white rounded-full"><Plus className="w-4 h-4 mr-1" />Tambah</Button>}
      </PageHeader>

      <div className="mb-4"><EventSelect value={eventId} onChange={setEventId} includeAll={false} /></div>

      {can(2) && eventId && (
        <div className="card-lux p-4 mb-4" data-testid="rundown-quickadd">
          <div className="flex items-center gap-2 mb-3"><Zap className="w-4 h-4 text-gold-dark" /><h3 className="font-heading text-lg text-burgundy">Tambah Rundown Cepat</h3></div>
          <div className="grid grid-cols-2 md:grid-cols-12 gap-2 items-end">
            <div className="col-span-1 md:col-span-2">
              <Label className="text-[11px] font-semibold text-[#4A4A4A] mb-1 block">Jam Mulai</Label>
              <div className="flex gap-1">
                <Select value={qa.hour} onValueChange={(v) => setQa({ ...qa, hour: v })}><SelectTrigger className="bg-white" data-testid="qa-hour"><SelectValue /></SelectTrigger><SelectContent className="max-h-60">{HOURS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent></Select>
                <Select value={qa.minute} onValueChange={(v) => setQa({ ...qa, minute: v })}><SelectTrigger className="bg-white" data-testid="qa-minute"><SelectValue /></SelectTrigger><SelectContent className="max-h-60">{MINUTES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div className="col-span-1 md:col-span-2">
              <Label className="text-[11px] font-semibold text-[#4A4A4A] mb-1 block">Jam Selesai</Label>
              <div className="flex gap-1">
                <Select value={qa.endHour || "none"} onValueChange={(v) => setQa({ ...qa, endHour: v === "none" ? "" : v })}><SelectTrigger className="bg-white" data-testid="qa-endhour"><SelectValue placeholder="--" /></SelectTrigger><SelectContent className="max-h-60"><SelectItem value="none">--</SelectItem>{HOURS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent></Select>
                <Select value={qa.endMinute || "00"} onValueChange={(v) => setQa({ ...qa, endMinute: v })}><SelectTrigger className="bg-white" data-testid="qa-endminute"><SelectValue /></SelectTrigger><SelectContent className="max-h-60">{MINUTES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div className="col-span-2 md:col-span-3"><Label className="text-[11px] font-semibold text-[#4A4A4A] mb-1 block">Aktivitas</Label><Input value={qa.activity} onChange={(e) => setQa({ ...qa, activity: e.target.value })} placeholder="mis. Akad Nikah" className="bg-white" data-testid="qa-activity" /></div>
            <div className="col-span-1 md:col-span-2"><Label className="text-[11px] font-semibold text-[#4A4A4A] mb-1 block">Lokasi</Label><Input value={qa.location} onChange={(e) => setQa({ ...qa, location: e.target.value })} className="bg-white" data-testid="qa-location" /></div>
            <div className="col-span-1 md:col-span-2"><Label className="text-[11px] font-semibold text-[#4A4A4A] mb-1 block">PIC</Label><Input value={qa.pic} onChange={(e) => setQa({ ...qa, pic: e.target.value })} className="bg-white" data-testid="qa-pic" /></div>
            <div className="col-span-2 md:col-span-1"><Button onClick={quickAdd} data-testid="rundown-quickadd-btn" className="w-full bg-burgundy hover:bg-burgundy-dark text-white rounded-full"><Plus className="w-4 h-4" /></Button></div>
          </div>
        </div>
      )}

      <div className="card-lux overflow-hidden print:shadow-none" id="rundown-print-area">
        {ev && <div className="px-6 py-4 border-b border-gold/20"><h2 className="font-heading text-2xl text-burgundy">Rundown — {ev.groom} & {ev.bride}</h2><p className="text-xs text-[#7A7A7A]">{fmtDate(ev.wedding_date)} • {ev.venue}</p></div>}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-beige/60 hover:bg-beige/60 border-gold/20">
                <TableHead className="text-[11px] uppercase tracking-wider text-[#4A4A4A]">Waktu</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-[#4A4A4A]">Aktivitas</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-[#4A4A4A]">PIC</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-[#4A4A4A]">Lokasi</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-[#4A4A4A]">Vendor</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-[#4A4A4A]">Status</TableHead>
                {can(2) && <TableHead className="text-right text-[11px] uppercase tracking-wider text-[#4A4A4A] print:hidden">Aksi</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-[#7A7A7A]">Belum ada rundown.</TableCell></TableRow>
              ) : items.map((r, i) => (
                <TableRow key={r.id} className="border-gold/10 hover:bg-beige/30" data-testid="rundown-row">
                  <TableCell className="font-mono font-semibold text-burgundy whitespace-nowrap">{r.time}{r.end_time ? ` – ${r.end_time}` : ""}</TableCell>
                  <TableCell className="text-sm font-medium text-[#1A1A1A]">{r.activity}</TableCell>
                  <TableCell className="text-sm text-[#4A4A4A]">{r.pic || "-"}</TableCell>
                  <TableCell className="text-sm text-[#4A4A4A]">{r.location || "-"}</TableCell>
                  <TableCell className="text-sm text-[#4A4A4A]">{r.vendor || "-"}</TableCell>
                  <TableCell>{(() => { const st = rundownStatus(ev, r, now); return st ? <RundownBadge status={st} /> : <button onClick={() => can(2) && cycleStatus(r)} data-testid="rundown-status-toggle"><StatusBadge status={r.status} /></button>; })()}</TableCell>
                  {can(2) && (
                    <TableCell className="text-right whitespace-nowrap print:hidden">
                      <button onClick={() => swap(i, i - 1)} className="p-1 text-[#7A7A7A] hover:text-burgundy" data-testid="rundown-up"><ChevronUp className="w-4 h-4" /></button>
                      <button onClick={() => swap(i, i + 1)} className="p-1 text-[#7A7A7A] hover:text-burgundy" data-testid="rundown-down"><ChevronDown className="w-4 h-4" /></button>
                      <button onClick={() => { setEditing(r); setFormOpen(true); }} className="p-1 text-[#7A7A7A] hover:text-burgundy" data-testid="rundown-edit"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => del(r.id)} className="p-1 text-[#7A7A7A] hover:text-red-600" data-testid="rundown-delete"><Trash2 className="w-4 h-4" /></button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <ResourceForm open={formOpen} onOpenChange={setFormOpen} fields={fields} initial={editing} resource="rundown" onSaved={load} defaults={{ event_id: eventId }} title={editing ? "Edit Rundown" : "Tambah Rundown"} />
    </div>
  );
}
